import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../../../../../store';
import {
  getChiaveRichiestaAppuntamenti,
  ParametriRichiestaAppuntamenti,
} from '../../../utility_objects/ChiaveRichiestaAppuntamenti';
import { DefaultSelectedFiltersState } from '../store/interface';

/** Filtri dell'agenda negozio che decidono quali appuntamenti caricare. */
export type FiltriAppuntamentiDefault = Pick<
  DefaultSelectedFiltersState,
  | 'selectedDate'
  | 'view'
  | 'selectedServices'
  | 'selectedStudi'
  | 'selectedUsers'
  | 'selectedPointOfSale'
> & {
  /** Email del cliente filtrato, se c'è */
  email?: string;
};

/**
 * Parametri della GetAll dell'agenda negozio. Li usano il caricamento e il
 * confronto con la lista in redux (selectAppuntamentiDefaultPronti): costruiti
 * in un posto solo, la chiave della pagina e quella della richiesta coincidono.
 */
export function getParametriAppuntamentiDefault({
  selectedDate,
  view,
  selectedServices,
  selectedStudi,
  selectedUsers,
  selectedPointOfSale,
  email,
}: FiltriAppuntamentiDefault): ParametriRichiestaAppuntamenti {
  return {
    selectedDate,
    checkedServices: selectedServices,
    checkedUsers: selectedUsers.map((user) => user.id),
    checkedPointOfSales: selectedPointOfSale ? [selectedPointOfSale.id] : [],
    checkedStudi: selectedStudi,
    view,
    email,
  };
}

// Ricalcolata solo quando cambia uno dei filtri, non a ogni azione dello store
const selectChiaveAppuntamentiDefault = createSelector(
  [
    (state: RootState) => state.bluBookingDefaultCalendar.selectedDate,
    (state: RootState) => state.bluBookingDefaultCalendar.view,
    (state: RootState) => state.bluBookingDefaultCalendar.selectedServices,
    (state: RootState) => state.bluBookingDefaultCalendar.selectedStudi,
    (state: RootState) => state.bluBookingDefaultCalendar.selectedUsers,
    (state: RootState) => state.bluBookingDefaultCalendar.selectedPointOfSale,
    (state: RootState) => state.bluBookingDefaultCalendar.client?.email,
  ],
  (
    selectedDate,
    view,
    selectedServices,
    selectedStudi,
    selectedUsers,
    selectedPointOfSale,
    email
  ) =>
    getChiaveRichiestaAppuntamenti(
      getParametriAppuntamentiDefault({
        selectedDate,
        view,
        selectedServices,
        selectedStudi,
        selectedUsers,
        selectedPointOfSale,
        email,
      })
    )
);

/**
 * La lista appuntamenti in redux è quella della data e dei filtri correnti
 * dell'agenda negozio. Il caricamento finito non basta: nei 50 ms prima della
 * richiesta, e finché la risposta nuova non arriva, la lista è quella vecchia.
 * Dopo un errore la lista vuota ha la chiave giusta, quindi è pronta.
 */
export const selectAppuntamentiDefaultPronti = (state: RootState): boolean =>
  state.bluBookingaAppointments.chiaveRichiesta ===
  selectChiaveAppuntamentiDefault(state);
